import { FC } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { OrgCountBarChart } from "components/Charts/OrgCountBarChart";
import { useDirectorOrgMetrics } from "../useDirectorOrgMetrics";
import { getMetricsDateRangeLabel, MetricsDateRange } from "../metricsDateRange";
import { MetricsDateRangeFilter } from "./MetricsDateRangeFilter";

type Props = {
  orgIds: string[];
  sinceDate: string;
  dateRange: MetricsDateRange;
  onDateRangeChange: (value: MetricsDateRange) => void;
};

export const DirectorOrgMetricsCharts: FC<Props> = ({
  orgIds,
  sinceDate,
  dateRange,
  onDateRangeChange,
}) => {
  const { learnersChartData, coachesChartData, feedbacksChartData, loading, error } =
    useDirectorOrgMetrics(orgIds, sinceDate);

  if (orgIds.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        No organizations available for director metrics.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ mt: 2 }}>
        Could not load organization metrics.
      </Typography>
    );
  }

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} lg={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Learners per organization
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Total learners assigned to each organization, with those who have
              accepted the terms of use.
            </Typography>
            <OrgCountBarChart
              data={learnersChartData}
              valueLabel="Learners"
              series={[
                { key: "learners", label: "Learners", color: "#1976d2" },
                {
                  key: "acceptedTerms",
                  label: "Accepted terms",
                  color: "#2e7d32",
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} lg={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Coaches per organization
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Total coaches linked to each organization, with those who have
              accepted the terms of use.
            </Typography>
            <OrgCountBarChart
              data={coachesChartData}
              valueLabel="Coaches"
              series={[
                { key: "coaches", label: "Coaches", color: "#ed6c02" },
                {
                  key: "acceptedTerms",
                  label: "Accepted terms",
                  color: "#2e7d32",
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
        >
          <Typography variant="body2" color="text.secondary">
            Filter feedback records by period.
          </Typography>
          <MetricsDateRangeFilter
            value={dateRange}
            onChange={onDateRangeChange}
          />
        </Stack>
      </Grid>
      <Grid item xs={12} lg={6}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Feedbacks per organization
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Feedback records submitted in each organization during the{" "}
              {getMetricsDateRangeLabel(dateRange).toLowerCase()}.
            </Typography>
            <OrgCountBarChart
              data={feedbacksChartData}
              valueLabel="Feedbacks"
              color="#2e7d32"
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
